import React from 'react';

/**
 * Service Invoice layout per the BENTA POS script (client spec).
 *
 * Format only — every figure comes from the fiscal engine exactly as computed;
 * nothing is recalculated here. Sections that only apply to a discounted sale
 * (TAX EXEMPT / Less: DISCOUNT / beneficiary block) are omitted when absent, so
 * the same component prints both the discounted and undiscounted variants.
 */
const peso = (n) => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const Divider = () => <div className="divider" />;

// Tailwind is NOT available in the print window -- it is a blank document built
// by document.write, carrying only the inline <style> block. Anything this
// receipt needs in order to print correctly therefore has to be an inline style
// or a class that block defines. Presentational Tailwind classes here were
// silently dropped on paper: the merchant name and SERVICE INVOICE were written
// bold and printed at body weight.
const FILL_ROW = { display: 'flex', alignItems: 'baseline', gap: 4, margin: '4px 0' };
const FILL = { flex: 1, borderBottom: '1px solid #000', height: '0.85em' };
const EMPH = { fontWeight: 700, fontSize: '1.08em' };
const Row = ({ label, value, bold }) => (
  <div className="item" style={bold ? EMPH : undefined}>
    <span>{label}</span><span>{value}</span>
  </div>
);
const Blank = ({ label }) => (
  <div style={FILL_ROW}><span>{label}:</span><span style={FILL} /></div>
);

const SERVICE_TYPE_LABEL = { dine_in: 'Dine In', take_out: 'Take Out', delivery: 'Delivery' };

export default function ServiceInvoice({ data }) {
  if (!data) return null;
  const bir = data.bir || {};
  const disc = data.bir_discount || {};
  const vendor = data.pos_vendor || {};
  const itemCount = (data.items || []).reduce((s, i) => s + (i.quantity || 0), 0);

  // An outlet is only fit to issue a fiscal invoice once BIR registration is
  // switched on AND the identifiers Sec 113(B)(1) requires are actually there.
  // Each of those lines is rendered only when its own value is present, so an
  // unconfigured outlet used to print a numbered SERVICE INVOICE with all of
  // them quietly missing and nothing to say it was not a real one.
  const missingBirFields = [
    [bir.tin, 'TIN'],
    [bir.min_number, 'MIN'],
    [bir.pos_serial_number, 'Serial No.'],
    [bir.ptu_number, 'PTU'],
  ].filter(([v]) => !v).map(([, label]) => label);
  const notRegistered = bir.is_bir_enabled === false || missingBirFields.length > 0;

  const hasStatutory = !!disc.discount_type && ['SENIOR', 'PWD'].includes(disc.discount_type);
  const vatRemoved = Number(disc.vat_amount_removed || 0);
  const discountAmt = Number(data.total_discount || data.discount_amount || 0);
  const dt = new Date(data.created_at);

  return (
    <div className="text-xs leading-tight">
      {notRegistered && !bir.is_training_mode && (
        <div style={{ border: '1px solid #000', padding: 6, marginBottom: 8,
                      textAlign: 'center', fontWeight: 700 }}>
          NOT REGISTERED WITH BIR<br />THIS IS NOT A VALID INVOICE
        </div>
      )}

      {bir.is_training_mode && (
        <div style={{ border: '1px solid #000', padding: 6, marginBottom: 8,
                      textAlign: 'center', fontWeight: 700 }}>
          TRAINING MODE<br />THIS IS NOT AN OFFICIAL RECEIPT
        </div>
      )}

      {/* Merchant header */}
      <div className="header" style={{ textAlign: 'center' }}>
        <div style={EMPH}>{bir.trade_name || data.outlet_name}</div>
        {bir.registered_business_name && <div>{bir.registered_business_name}</div>}
        <div>{bir.registered_address || data.outlet_address}</div>
        {bir.tin && (
          <div>
            {bir.vat_type === 'VAT' ? 'VAT Reg. TIN' : 'NON-VAT Reg. TIN'}: {bir.tin}
            {bir.branch_code ? `-${bir.branch_code}` : ''}
          </div>
        )}
        {bir.min_number && <div>MIN:{bir.min_number}</div>}
        {bir.pos_serial_number && <div>SN:{bir.pos_serial_number}</div>}
        {bir.ptu_number && <div>PTU:{bir.ptu_number}</div>}
        <div style={{ ...EMPH, marginTop: 8 }}>SERVICE INVOICE</div>
        {data.receipt_number && <div>SI #: {data.receipt_number}</div>}
      </div>

      <Divider />

      {/* Order block */}
      <Row label={`Order No: ${data.ticket_number || ''}`} value={`Order Type: ${SERVICE_TYPE_LABEL[data.service_type] || 'Dine In'}`} />
      <Row label={`Table No: ${data.table_name || '-'}`} value={`Guest: ${data.guest_count ?? ''}`} />
      <div className="item"><span>Date/Time: {dt.toLocaleDateString()}</span><span>{dt.toLocaleTimeString()}</span></div>
      <Row label={`Cashier: ${data.cashier_name || '-'}`} value={`Server: ${data.server_name || '-'}`} />

      <Divider />

      {/* Customer block — blank lines for the buyer to fill in */}
      <Blank label="Name" />
      <Blank label="Address" />
      <Blank label="TIN" />
      <Blank label="Business Style" />

      <Divider />

      <Row label="QTY  Item Description" value="Amount" />
      <Divider />
      {(data.items || []).map((i, idx) => (
        <div key={idx}>
          <Row label={`${i.quantity} ${i.name}`} value={peso(i.line_total)} />
          {i.discount_amount > 0 && (
            <div className="item" style={{ paddingLeft: 12 }}><span>   Less: item discount</span><span>-{peso(i.discount_amount)}</span></div>
          )}
        </div>
      ))}

      <Divider />

      {/* Totals */}
      <Row label={`${itemCount}  Subtotal`} value={peso(data.gross_subtotal ?? data.subtotal)} />
      {vatRemoved > 0 && <Row label="TAX EXEMPT" value={`-${peso(vatRemoved)}`} />}
      {discountAmt > 0 && (
        <Row label={`Less: ${(disc.discount_type === 'CUSTOM' ? disc.reason : disc.discount_type) || 'DISCOUNT'} DISCOUNT`} value={`-${peso(discountAmt)}`} />
      )}
      {(disc.beneficiaries || []).length > 0 ? (
        // BIR wants the NAME, ID and signature of each person the statutory
        // discount was granted to. A bill can carry several (a senior dining
        // with a PWD), and this printed only a count.
        //
        // Inline styles, not Tailwind: the print window is a blank document
        // built by document.write and carries only its own <style> block.
        <div style={{ marginTop: 6 }}>
          {disc.beneficiaries.map((b, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div>{b.discount_type} {Number(b.discount_rate || 0)}% -- {b.beneficiary_name || ''}</div>
              <div>ID: {b.beneficiary_id_number || ''}</div>
              <Blank label="Signature" />
            </div>
          ))}
        </div>
      ) : (
        hasStatutory && data.beneficiary_count > 0 && (
          <div style={{ fontSize: '0.95em' }}>({disc.discount_type}: {data.beneficiary_count})</div>
        )
      )}
      {data.template?.show_service_charge && Number(data.service_charge || 0) > 0 && (
        <Row label="Service Charge:" value={peso(data.service_charge)} />
      )}
      <Row label="Net Total" value={peso(data.total)} bold />

      <Divider />

      {/* Payment */}
      <Row label={(data.payment_method || 'CASH').toUpperCase()} value={peso(data.total)} />
      {data.tendered > 0 && <Row label="Cash Tendered" value={peso(data.tendered)} />}
      {data.change_given > 0 && <Row label="Change:" value={peso(data.change_given)} />}
      {data.payment_reference && <Row label="Ref/Card No:" value={data.payment_reference} />}

      <Divider />

      {/* VAT breakdown — straight from the engine */}
      {data.template?.show_vat_breakdown !== false && (
        <>
          <Row label="VATable Sales" value={peso(data.vatable_sales)} />
          <Row label="VAT 12%" value={peso(data.vat_amount)} />
          <Row label="VAT Exempted" value={peso(data.vat_exempt_sales)} />
          <Row label="VAT Zero Rated" value={peso(data.zero_rated_sales)} />
          {/*
            Make the block reconcile to what was actually paid, and say why it
            otherwise would not. Two figures sit outside the four lines above:

            - the SERVICE CHARGE, which this system does not treat as part of
              the VAT base (the RMC 29-70 trust-fund reading, and RA 11360 now
              sends 100% of it to the employees);
            - a STATUTORY discount, because RA 9994 reports the VAT-exempt sale
              at its PRE-discount amount and the 20% is a deduction from gross
              income -- so on a senior bill the four lines legitimately sum to
              MORE than the customer paid.

            A commercial discount needs no line here: it is already inside
            VATable Sales, which is computed net of it (RR 16-2005 sec 4.106-9).
          */}
          {hasStatutory && Number(disc.discount_amount || 0) > 0 && (
            // Conventional BIR wording, and deliberately NOT the same label as
            // the deduction in the totals block above -- two lines reading
            // "Less: SENIOR DISCOUNT" and "Less: SENIOR Discount" look like a
            // duplication bug rather than a running total and a tax summary.
            <Row label="Less: SC/PWD Discount" value={`-${peso(disc.discount_amount)}`} />
          )}
          {Number(data.service_charge || 0) > 0 && (
            <Row label="Service Charge (not VAT-taxed)" value={peso(data.service_charge)} />
          )}
          <Row label="Total Amount Due" value={peso(data.total)} bold />
          <Divider />
        </>
      )}

      {/* Beneficiary block — discounted sales only */}
      {hasStatutory && (
        <>
          {disc.beneficiary_name
            ? <div>Name: {disc.beneficiary_name}</div>
            : <Blank label="Name" />}
          <Blank label="Address" />
          {disc.beneficiary_id_number
            ? <div>SC/PWD ID: {disc.beneficiary_id_number}</div>
            : <Blank label="SC/PWD ID" />}
          <Blank label="Signature" />
          <Divider />
        </>
      )}

      {/* POS vendor block */}
      <div style={{ marginTop: 8 }}>
        <div>POS Vendor Details:</div>
        <div>{vendor.name || 'BENTA POS'}</div>
        {vendor.address && <div>{vendor.address}</div>}
        {vendor.tin && <div>TIN: {vendor.tin}</div>}
        {vendor.accreditation_number && <div>Accred. No: {vendor.accreditation_number}</div>}
        {vendor.date_issued && <div>Date Issued: {new Date(vendor.date_issued).toLocaleDateString()}</div>}
        <Blank label="Valid Until" />
      </div>

      <div className="footer" style={{ textAlign: 'center', marginTop: 8 }}>
        <div>{bir.is_training_mode ? 'THIS IS NOT AN OFFICIAL RECEIPT' : 'THIS SERVES AS YOUR SERVICE INVOICE.'}</div>
        <div>{data.template?.footer_text || 'Thank you and Please Come Again'}</div>
        {bir.bir_footer_text && <div style={{ marginTop: 4 }}>{bir.bir_footer_text}</div>}
      </div>
    </div>
  );
}
